/**
 * Protected Route Component
 * 
 * Wraps routes that require authentication and/or specific permissions.
 * Redirects unauthenticated users to login and unauthorized users to 403 page.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/shared/infra/hooks/useAuth';
import { usePermission } from '@/shared/infra/hooks/usePermission';
import { Permission } from '@/shared/application/types/authTypes';
import { setPostLoginRedirect } from '@/shared/infra/auth/postLoginRedirect';

interface ProtectedRouteProps {
  /** Content to render if authorized */
  children: React.ReactNode;

  /** Single permission required to access this route */
  requiredPermission?: Permission;

  /** User needs at least one of these permissions */
  anyPermission?: Permission[];

  /** User needs all of these permissions */
  allPermissions?: Permission[];

  /** Route is restricted to internal VT super admins (normalized roleKey === 'super_admin') */
  requireSuperAdmin?: boolean;

  /** Only check authentication, not permissions (default: true) */
  requireAuth?: boolean;

  /** Where to redirect if not authenticated (default: '/login') */
  redirectTo?: string;
}

/**
 * Component that protects routes based on authentication and permissions
 * 
 * @example
 * ```tsx
 * import { Resource, Action, createPermission } from '@/shared/application/types/authTypes';
 * 
 * // Require authentication only
 * <ProtectedRoute>
 *   <ProfilePage />
 * </ProtectedRoute>
 * 
 * // Require specific permission
 * <ProtectedRoute requiredPermission={createPermission(Resource.SECTION2, Action.READ)}>
 *   <Section2Page />
 * </ProtectedRoute>
 * 
 * // Require any of multiple permissions
 * <ProtectedRoute anyPermission={[
 *   createPermission(Resource.DASHBOARD, Action.READ),
 *   createPermission(Resource.DASHBOARD, Action.EDIT)
 * ]}>
 *   <DashboardPage />
 * </ProtectedRoute>
 * ```
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
  anyPermission,
  allPermissions,
  requireSuperAdmin = false,
  requireAuth = true,
  redirectTo = '/login',
}) => {
  const { isAuthenticated, loading, hasSelectedAccount, role } = useAuth();
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermission();
  const location = useLocation();

  // Check if user is Super Admin — keyed off the normalized roleKey (VTK-1828), NOT the display
  // string `vtAppRole`. The broker sends the role snake_case (`super_admin`), so an exact match on
  // the display name "Super Admin" silently fails and hides super-admin-only UI (storage/digitization).
  const isSuperAdmin = role === 'super_admin';

  // Show nothing while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  // Check authentication first. Remember where they were heading: without this the destination is
  // thrown away, and someone who followed a link straight to a record lands on the dashboard and has
  // to find their way back (VTK-1918). `state` alone will not do — the OAuth path leaves the app and
  // returns on a fresh page load, so this is persisted.
  if (requireAuth && !isAuthenticated) {
    setPostLoginRedirect(`${location.pathname}${location.search}`);
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Redirect Super Admin to account-selection if hasn't selected an account (except on account-selection itself)
  if (isAuthenticated && isSuperAdmin && !hasSelectedAccount && location.pathname !== '/account-selection') {
    // Same again for the account interstitial, which sits between a super admin and their
    // destination. Kept, not consumed — account selection is what finally spends it.
    setPostLoginRedirect(`${location.pathname}${location.search}`);
    return <Navigate to="/account-selection" state={{ from: location }} replace />;
  }

  // Routes restricted to internal VT super admins
  if (requireSuperAdmin && !isSuperAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }

  // If no permissions specified, just check auth and allow access
  if (!requiredPermission && !anyPermission && !allPermissions) {
    return <>{children}</>;
  }

  // Check permissions
  let hasAccess = false;

  if (requiredPermission) {
    hasAccess = hasPermission(requiredPermission);
  } else if (anyPermission && anyPermission.length > 0) {
    hasAccess = hasAnyPermission(anyPermission);
  } else if (allPermissions && allPermissions.length > 0) {
    hasAccess = hasAllPermissions(allPermissions);
  }

  // Redirect to unauthorized page if no access
  if (!hasAccess) {
    return <Navigate to="/unauthorized" replace />;
  }

  // User is authenticated and has required permissions
  return <>{children}</>;
};
